<template>
	<div class="rows">
		<ValidationProvider
			ref="provider"
			:vid="`${computedName}-${generatedId}`"
			:name="options.label"
			:rules="options.rules"
			v-slot="{ errors, classes }"
			slim
		>
			<div :class="[options.wrapperClass, { 'mb-8px': errors[0] === undefined }]">
				<label class="select-label d-table mb-10px relative" v-if="hasLabel">
					{{ options.label }}
					<span v-if="options.tooltip" v-tooltip="options.tooltipText" class="info"></span>
				</label>
				<DatePicker
					:value="getData()"
					@input="handleInput($event)"
					@input-error="handleInputError()"
					value-type="format"
					:formatter="formatter"
					:class="classes"
					:id="generatedId"
					:name="name"
					:type="options.type"
					:placeholder="options.label"
					:disabled-date="disabledDate"
					:default-value="computedDefault"
					:disabled="isDisabled"
					@open="focusInput"
					ref="date"
					:input-attr="{
						'data-hj-allow': true
					}"
				/>
				<span
					:class="[
						'error_message error_message--broke',
						options.className,
						{ 'mb-8px': type === 'contracting' },
					]"
					v-if="errors[0]"
				>
					{{ errors[0] }}
				</span>
			</div>
		</ValidationProvider>
	</div>
</template>

<script>
import shared from '@/mixins/shared';
import moment from 'moment';
import { mapGetters } from 'vuex';

export default {
	mixins: [shared],
	data() {
		return {
			formatter: {
				stringify: date => {
					return date ? moment(date).format('yyyy.MM.DD.') : '';
				},
				parse: value => {
					if (value.indexOf('.') === -1 && value.indexOf('-') === -1) {
						const year = value.slice(0, 4);
						const month = value.slice(4, 6);
						const day = value.slice(6, 8);

						value = `${year}-${month}-${day}`;
					}
					return value ? moment(value, 'yyyy-MM-DD').toDate() : null;
				},
			},
		};
	},
	computed: {
		...mapGetters('contracting', ['getDisabledInputs']),
		isDisabled() {
			if (this.type === 'contracting') {
				return (
					this.disabled ||
					this.getDisabledInputs.includes(
						`${this.options.input.parent}-${this.options.input.group}-${this.options.input.prop}`,
					)
				);
			}

			return this.disabled;
		},
		computedMax() {
			return moment().add(this.options.max, 'd').format('yyyy-MM-DD');
		},
		computedMin() {
			return moment().add(this.options.min, 'd').format('yyyy-MM-DD');
		},
		hasLabel() {
			return !_.has(this.options, 'hasLabel') ? true : this.options.hasLabel;
		},
		computedDefault() {
			if (_.has(this.options, 'defaultValue')) {
				return this.parseOptionsDate();
			}

			if (_.has(this.options, 'fromDateAttr')) {
				const splitted = this.options.fromDateAttr.split('.');

				if (this.$store.state[splitted[0]][splitted[1]] !== null) {
					return moment(this.$store.state[splitted[0]][splitted[1]]).add(1, 'd');
				}
			}

			return moment();
		},
	},
	methods: {
		parseOptionsDate() {
			const splitted = this.options.defaultValue.split('|');
			const isMinus = parseInt(splitted[0]) < 0;

			if (isMinus) {
				return moment()
					.subtract(Math.abs(parseInt(splitted[0])), splitted[1])
					.format('yyyy-MM-DD');
			} else {
				return moment().add(parseInt(splitted[0]), splitted[1]).format('yyyy-MM-DD');
			}
		},
		handleInput(date) {
			this.setData(this.computedValue(date));
			this.$emit('change', date);
		},
		disabledDate(date) {
			if (_.has(this.options, 'fromDateAttr')) {
				const splitted = this.options.fromDateAttr.split('.');

				if (this.$store.state[splitted[0]][splitted[1]] !== null) {
					const isBefore = moment(date).isSameOrBefore(moment(this.$store.state[splitted[0]][splitted[1]]));

					if (isBefore) {
						return true;
					}
				}
			}

			if (!_.has(this.options, 'min') && !_.has(this.options, 'max')) {
				return false;
			}

			if (!_.has(this.options, 'min') && _.has(this.options, 'max')) {
				const before = moment().subtract(this.options.max, 'y').format('yyyy-MM-DD');
				return !moment(date).isSameOrBefore(before);
			}

			if (_.has(this.options, 'min') && !_.has(this.options, 'max')) {
				const after = moment().add(this.options.min, 'y').format('yyyy-MM-DD');
				return !moment(date).isSameOrAfter(after);
			}

			return !moment(date).isBetween(
				moment(this.computedMin).subtract(1, 'd').format('yyyy-MM-DD'),
				this.computedMax,
			);
		},
		focusInput() {
			this.$refs.date.focus();
		},
		async handleInputError() {
			await this.$refs.provider.validate();
		},
	},
};
</script>

<style lang="scss" scoped>
.mx-datepicker {
	width: 100%;

	&::after {
		right: 35px !important;
	}
}
</style>
